import { useState } from 'react';
import { Input, Button } from '@/components/ui';
import { AddressFields } from '@/features/projects/components/AddressFields';
import { formatPhoneInput } from '@/lib/phoneUtils';
import type { SupplierLocationView, CreateSupplierLocationInput, UpdateSupplierLocationInput } from '../types';

// All address fields optional for supplier locations
const OPTIONAL_ADDRESS_FIELDS = {
  streetAddress: false,
  city: false,
  province: false,
  postalCode: false,
  country: false,
};

interface SupplierLocationFormProps {
  /** Existing location for edit mode, undefined for create mode */
  location?: SupplierLocationView;
  /** Business ID required for create */
  businessId: string;
  /** Called when form is submitted successfully */
  onSubmit: (data: CreateSupplierLocationInput | UpdateSupplierLocationInput) => Promise<void>;
  /** Called when form is cancelled */
  onCancel: () => void;
  /** Whether the form is in a loading/submitting state */
  isSubmitting?: boolean;
}

interface FormData {
  locationName: string;
  streetAddress: string;
  unit: string;
  city: string;
  province: string;
  postalCode: string;
  country: string;
  phone: string;
  email: string;
  contactFirstName: string;
  contactLastName: string;
  notes: string;
}

export function SupplierLocationForm({
  location,
  businessId,
  onSubmit,
  onCancel,
  isSubmitting = false,
}: SupplierLocationFormProps) {
  const isEditMode = !!location;

  const [formData, setFormData] = useState<FormData>({
    locationName: location?.locationName || '',
    streetAddress: location?.streetAddress || '',
    unit: location?.unit || '',
    city: location?.city || '',
    province: location?.province || 'ON',
    postalCode: location?.postalCode || '',
    country: location?.country || 'Canada',
    phone: location?.phone || '',
    email: location?.email || '',
    contactFirstName: location?.contactFirstName || '',
    contactLastName: location?.contactLastName || '',
    notes: location?.notes || '',
  });

  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  const handleChange = (field: string, value: string) => {
    // Handle phone formatting
    if (field === 'phone') {
      value = formatPhoneInput(value);
    }

    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when field is modified
    if (errors[field as keyof FormData]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleAddressChange = (field: string, value: string) => {
    // AddressFields uses field names like "streetAddress" directly (no prefix)
    // when fieldPrefix is empty string
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormData]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    // Email validation (if provided)
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    const data: CreateSupplierLocationInput | UpdateSupplierLocationInput = {
      location_name: formData.locationName || null,
      street_address: formData.streetAddress || null,
      unit: formData.unit || null,
      city: formData.city || null,
      province: formData.province || null,
      postal_code: formData.postalCode || null,
      country: formData.country || null,
      phone: formData.phone || null,
      email: formData.email || null,
      contact_first_name: formData.contactFirstName || null,
      contact_last_name: formData.contactLastName || null,
      notes: formData.notes || null,
    };

    // Add business_id for create mode
    if (!isEditMode) {
      (data as CreateSupplierLocationInput).business_id = businessId;
    }

    await onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Location Name */}
      <Input
        label="Location Name (Optional)"
        name="locationName"
        value={formData.locationName}
        onChange={(e) => handleChange('locationName', e.target.value)}
        placeholder="e.g., Downtown, Warehouse, Head Office"
        error={errors.locationName}
        disabled={isSubmitting}
      />

      {/* Address Section */}
      <div>
        <h4 className="text-sm font-medium text-secondary-700 mb-3">Address</h4>
        <AddressFields
          streetAddress={formData.streetAddress}
          unit={formData.unit}
          city={formData.city}
          province={formData.province}
          postalCode={formData.postalCode}
          country={formData.country}
          onChange={handleAddressChange}
          errors={{
            streetAddress: errors.streetAddress,
            unit: errors.unit,
            city: errors.city,
            province: errors.province,
            postalCode: errors.postalCode,
            country: errors.country,
          }}
          disabled={isSubmitting}
          fieldPrefix=""
          requiredFields={OPTIONAL_ADDRESS_FIELDS}
        />
      </div>

      {/* Contact Section */}
      <div>
        <h4 className="text-sm font-medium text-secondary-700 mb-3">Contact (Optional)</h4>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="First Name"
              name="contactFirstName"
              value={formData.contactFirstName}
              onChange={(e) => handleChange('contactFirstName', e.target.value)}
              placeholder="John"
              error={errors.contactFirstName}
              disabled={isSubmitting}
            />
            <Input
              label="Last Name"
              name="contactLastName"
              value={formData.contactLastName}
              onChange={(e) => handleChange('contactLastName', e.target.value)}
              placeholder="Smith"
              error={errors.contactLastName}
              disabled={isSubmitting}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder="(416) 555-1234"
              error={errors.phone}
              disabled={isSubmitting}
            />
            <Input
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="contact@example.com"
              error={errors.email}
              disabled={isSubmitting}
            />
          </div>
        </div>
      </div>

      {/* Notes */}
      <div>
        <label htmlFor="notes" className="input-label">
          Notes (Optional)
        </label>
        <textarea
          id="notes"
          name="notes"
          value={formData.notes}
          onChange={(e) => handleChange('notes', e.target.value)}
          placeholder="Additional notes about this location..."
          rows={3}
          className="input-field resize-none"
          disabled={isSubmitting}
        />
      </div>

      {/* Form Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t border-secondary-200">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={isSubmitting}>
          {isSubmitting
            ? isEditMode
              ? 'Saving...'
              : 'Adding...'
            : isEditMode
              ? 'Save Changes'
              : 'Add Location'}
        </Button>
      </div>
    </form>
  );
}
